import React, { useEffect, useState } from "react";
import { motion, animate } from "framer-motion";
import { ArrowUpRight, TrendingUp, ShoppingBag, Utensils } from "lucide-react";

// A tiny, fully self-contained "live dashboard" that loops silently.
// Zero external assets — pure CSS + inline SVG + Framer Motion.
// Meant to sit next to the hero globe as social-proof-of-product.
const CHART = [8, 12, 9, 14, 11, 18, 16, 22, 20, 26, 24, 30];
const START_VALUE = 4162.4;
const END_VALUE = 4268.5;

// Two rows of demo activity that will scroll softly.
const ACTIVITY = [
    { id: "a", label: "Trader Joe's", cat: "Groceries", amt: -42.18, icon: ShoppingBag, tint: "#F9C77E" },
    { id: "b", label: "Payroll", cat: "Income", amt: 2140.00, icon: TrendingUp, tint: "#7DD3B0" },
    { id: "c", label: "Blue Bottle Coffee", cat: "Dining", amt: -6.75, icon: Utensils, tint: "#F28C8C" },
];

const AreaChartDraw = () => {
    // Convert chart data to SVG path
    const w = 220, h = 60;
    const max = Math.max(...CHART), min = Math.min(...CHART);
    const points = CHART.map((v, i) => {
        const x = (i / (CHART.length - 1)) * w;
        const y = h - ((v - min) / (max - min)) * (h - 8) - 4;
        return [x, y];
    });
    const linePath = points.map(([x, y], i) => `${i === 0 ? "M" : "L"} ${x} ${y}`).join(" ");
    const areaPath = `${linePath} L ${w} ${h} L 0 ${h} Z`;
    const last = points[points.length - 1];

    return (
        <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} className="block">
            <defs>
                <linearGradient id="live-chart-grad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#7DD3B0" stopOpacity={0.45} />
                    <stop offset="100%" stopColor="#7DD3B0" stopOpacity={0} />
                </linearGradient>
            </defs>
            <motion.path
                d={areaPath}
                fill="url(#live-chart-grad)"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.4 }}
            />
            <motion.path
                d={linePath}
                fill="none"
                stroke="#7DD3B0"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ duration: 1.6, ease: "easeInOut" }}
            />
            {/* Pulsing end dot */}
            <motion.circle
                cx={last[0]}
                cy={last[1]}
                r={4}
                fill="#7DD3B0"
                initial={{ opacity: 0 }}
                animate={{ opacity: [0, 1, 1] }}
                transition={{ duration: 1, delay: 1.6 }}
            />
            <motion.circle
                cx={last[0]}
                cy={last[1]}
                r={7}
                fill="#7DD3B0"
                animate={{ scale: [1, 2, 1], opacity: [0.5, 0, 0.5] }}
                transition={{ duration: 1.8, repeat: Infinity, delay: 1.8 }}
                style={{ transformOrigin: `${last[0]}px ${last[1]}px` }}
            />
        </svg>
    );
};

const LiveDashboardMockup = () => {
    // Loop the whole card animation by remounting with a changing key every ~7s.
    const [tick, setTick] = useState(0);
    useEffect(() => {
        const id = setInterval(() => setTick((t) => t + 1), 7000);
        return () => clearInterval(id);
    }, []);

    return (
        <motion.div
            initial={{ opacity: 0, y: -20, rotate: -3 }}
            animate={{ opacity: 1, y: 0, rotate: -3 }}
            transition={{ duration: 0.9, delay: 0.6, ease: "easeOut" }}
            className="block absolute top-14 right-4 md:top-16 md:right-8 lg:top-20 lg:right-12 w-[220px] sm:w-[260px] lg:w-[300px] z-10 pointer-events-none"
            data-testid="landing-live-dashboard"
        >
            <motion.div
                animate={{ y: [0, -8, 0], rotate: [-3, -2.5, -3] }}
                transition={{ duration: 6, ease: "easeInOut", repeat: Infinity }}
                key={tick}
                className="rounded-2xl overflow-hidden shadow-2xl"
                style={{
                    background: "linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(255,255,255,0.94) 100%)",
                    border: "1px solid rgba(255,255,255,0.35)",
                    backdropFilter: "blur(20px)",
                    WebkitBackdropFilter: "blur(20px)",
                    boxShadow: "0 30px 80px -20px rgba(0,0,0,0.45), 0 0 0 1px rgba(255,255,255,0.08)",
                }}
            >
                {/* Faux window chrome */}
                <div className="flex items-center gap-1.5 px-3 py-2.5 border-b border-black/5">
                    <span className="w-2 h-2 rounded-full bg-[#FF5F57]" />
                    <span className="w-2 h-2 rounded-full bg-[#FEBC2E]" />
                    <span className="w-2 h-2 rounded-full bg-[#28C840]" />
                    <div className="ml-2 flex items-center gap-1.5">
                        <span className="text-[9px] font-bold tracking-[0.18em] text-[#0D1E2E]/60 uppercase">Live</span>
                        <motion.span
                            animate={{ opacity: [1, 0.3, 1] }}
                            transition={{ duration: 1.4, repeat: Infinity }}
                            className="w-1.5 h-1.5 rounded-full bg-[#7DD3B0]"
                        />
                    </div>
                </div>

                {/* Balance block */}
                <div className="px-4 pt-3 pb-2">
                    <div className="text-[9px] font-bold tracking-[0.24em] text-[#0D1E2E]/50 uppercase">Net cash flow</div>
                    <div className="mt-1 flex items-baseline gap-1">
                        <span className="font-display font-extrabold text-[#0D1E2E] text-[26px] tabular-nums leading-none">
                            $<CountingNumber key={`num-${tick}`} from={START_VALUE} to={END_VALUE} />
                        </span>
                        <motion.span
                            animate={{ y: [0, -2, 0] }}
                            transition={{ duration: 1.6, repeat: Infinity }}
                            className="inline-flex items-center gap-0.5 text-[#2AA07A] text-[11px] font-bold"
                        >
                            <ArrowUpRight className="w-3 h-3" />
                            2.5%
                        </motion.span>
                    </div>
                </div>

                {/* Mini area chart */}
                <div className="px-2">
                    <AreaChartDraw key={`chart-${tick}`} />
                </div>

                {/* Activity rows */}
                <div className="px-3 pb-3 pt-1 space-y-1.5">
                    {ACTIVITY.map((a, i) => {
                        const Icon = a.icon;
                        return (
                            <motion.div
                                key={`${tick}-${a.id}`}
                                initial={{ opacity: 0, x: 12 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.5, delay: 1.4 + i * 0.25, ease: "easeOut" }}
                                className="flex items-center gap-2.5 rounded-xl px-2 py-1.5 bg-[#0D1E2E]/[0.03]"
                            >
                                <div
                                    className="w-6 h-6 rounded-lg grid place-items-center shrink-0"
                                    style={{ background: `${a.tint}22` }}
                                >
                                    <Icon className="w-3 h-3" style={{ color: a.tint }} />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="text-[11px] font-semibold text-[#0D1E2E] truncate leading-tight">{a.label}</div>
                                    <div className="text-[9px] text-[#0D1E2E]/50">{a.cat}</div>
                                </div>
                                <div className={`text-[11px] font-bold tabular-nums ${a.amt >= 0 ? "text-[#2AA07A]" : "text-[#0D1E2E]"}`}>
                                    {a.amt >= 0 ? "+" : "-"}${Math.abs(a.amt).toFixed(2)}
                                </div>
                            </motion.div>
                        );
                    })}
                </div>
            </motion.div>
        </motion.div>
    );
};

// Counting number that animates from `from` → `to` on mount.
const CountingNumber = ({ from, to, duration = 2 }) => {
    const [display, setDisplay] = useState(from);
    useEffect(() => {
        const controls = animate(from, to, {
            duration,
            ease: "easeOut",
            onUpdate: (v) => setDisplay(v),
        });
        return () => controls.stop();
    }, [from, to, duration]);
    return <>{display.toFixed(2)}</>;
};

export default LiveDashboardMockup;
